import {
  BackButton,
  Banner,
  getSvgByName,
  GUT_BODY_CONNECTIONS_COPY,
  GutBodyConnectionsBody,
  Icon,
  resolveGutBodyConnectionsSystems,
  Typography,
} from '@repo/ui';
import c from 'classnames';
import { BetaTag, ContainerSizes, PageContainer } from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useTrackPageView } from 'contexts/analytics/useTrackPageView';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useElementData from 'hooks/useElementData';
import useGutBodyProps from 'hooks/useGutBodyProps';
import useHashScroll from 'hooks/useHashScroll';
import useObserve from 'hooks/useObserve';
import useSelectOrgan from 'hooks/useSelectOrgan';
import { CSSProperties, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import useOrgans from 'services/organs/useOrgans';
import { organAnchorId } from 'views/Results/utils/organs';
import { useConditionsEducationalModal } from '../ConditionsEducationalModal/context';
import { ConditionsInfoModal } from '../ConditionsInfoModal';
import styles from './NewConditionMetricsList.module.css';
import OrganSection from './OrganSection';
import OrganSelector from './OrganSelector';

const INSTANT_SCROLL: ScrollIntoViewOptions = { behavior: 'instant' };

export default function NewConditionMetricsList() {
  const navigate = useNavigate();
  const { currentKitId, currentKit } = useCurrentKitAndTinyAccount();
  const { organs } = useOrgans(currentKitId);
  const selectOrgan = useSelectOrgan('conditions_home');

  const { isFeatureEnabled: isHAEMEnabled } = useFeature(
    'health_associations_educational_modal',
  );
  const { openReview } = useConditionsEducationalModal();

  const { age, pregnant, sex, colorVariant } = useGutBodyProps();

  // Resolved here too, so the sections list exactly the organs the map draws.
  const { listedSystems, bodyAgeGroup } = useMemo(
    () => resolveGutBodyConnectionsSystems({ organs, age, pregnant, sex }),
    [organs, age, pregnant, sex],
  );

  const { elRef, height: headerHeight } = useElementData();

  // Waits on the sections, which only exist once the organs have resolved.
  useHashScroll({
    elementsWithId: listedSystems,
    scrollSettings: INSTANT_SCROLL,
    ready: !!headerHeight,
  });

  const anchorIds = useMemo(
    () => listedSystems.map(({ organ }) => organAnchorId(organ.lookup_code)),
    [listedSystems],
  );
  const visibleIds = useObserve(anchorIds, {
    root: '#appWrapper',
    rootMargin: '-100px 0px -65% 0px',
  });
  const activeId = anchorIds.find(id => visibleIds.has(id)) ?? anchorIds[0];

  useTrackPageView(
    'Conditions Page Viewed',
    currentKit ? currentKitId : undefined,
    { experience: 'gut_body_connections' },
  );

  const goBack = () => navigate(-1);

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      {!isHAEMEnabled ? <ConditionsInfoModal /> : null}
      {/* The layout's own floating one is suppressed for this route, so it
          cannot hover over the organ selector. */}
      <BackButton
        goBack={goBack}
        className={c(styles.backButton, 'hide-on-desktop')}
      />
      <header className={styles.header} ref={elRef}>
        <Breadcrumb gutBodyConnections />
        <div className={styles.textsContainer}>
          <div className={styles.titleRow}>
            <Typography variant='heading-xl'>
              {GUT_BODY_CONNECTIONS_COPY.title}
            </Typography>
            {isHAEMEnabled ? (
              <button
                type='button'
                className={styles.infoButton}
                onClick={openReview}
                aria-label='Learn more about gut-body connections'
              >
                <Icon name='infoCircle' size='m' color='grey' />
              </button>
            ) : null}
            <BetaTag />
          </div>
          <Typography variant='body-s' color='grey'>
            {GUT_BODY_CONNECTIONS_COPY.subtitle}
          </Typography>
        </div>
        <Banner
          color='purple'
          title={GUT_BODY_CONNECTIONS_COPY.disclaimerTitle}
          content={GUT_BODY_CONNECTIONS_COPY.disclaimerBody}
          icon={getSvgByName('infoCircle')}
          className={styles.banner}
          iconContainerClassName={styles.bannerIconContainer}
          iconClassName={styles.bannerIcon}
          variant='full'
          size='sm'
        />
      </header>
      <OrganSelector
        organs={listedSystems.map(({ organ }) => organ)}
        activeId={activeId}
        age={age}
        pregnant={pregnant}
        colorVariant={colorVariant}
      />
      <div className={styles.content}>
        <div
          className={c(
            styles.bodyContainer,
            styles[bodyAgeGroup],
            'hide-on-mobile',
          )}
          style={{ top: headerHeight }}
        >
          <GutBodyConnectionsBody
            age={age}
            pregnant={pregnant}
            organs={organs}
            sex={sex}
            hoverVariant='pill'
            onOrganSelect={selectOrgan}
            fadeBody
            colorVariant={colorVariant}
          />
        </div>
        <div className={styles.organSectionsContainer}>
          {listedSystems.map(({ organ }) => (
            <OrganSection
              key={organ.id}
              organ={organ}
              age={age}
              pregnant={pregnant}
              colorVariant={colorVariant}
              style={
                {
                  '--conditions-header-height': `${headerHeight}px`,
                } as CSSProperties
              }
            />
          ))}
        </div>
      </div>
    </PageContainer>
  );
}
